import type React from 'react';
import { useCallback, useEffect } from 'react';
import { getStats } from '../api/admin';
import { useAsync } from '../api/useAsync';
import { AsyncBoundary, StatCard } from '../components/ui';

// Matches the server's ADMIN_STATS_CACHE_SEC default, so polling costs one
// upstream fan-out per interval at most.
const POLL_MS = 30_000;

export function DashboardPage(): React.JSX.Element {
  const load = useCallback(() => getStats(), []);
  const { data, loading, error, reload } = useAsync(load);

  useEffect(() => {
    const id = window.setInterval(reload, POLL_MS);
    return () => window.clearInterval(id);
  }, [reload]);

  const degraded = new Set(data?.degraded ?? []);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Visão geral</h1>
          <p className="page__subtitle">
            Atualiza a cada 30&nbsp;s.{' '}
            {data?.cached ? 'Exibindo valores em cache.' : 'Valores recém-consultados.'}
          </p>
        </div>
        <button type="button" className="btn" onClick={reload}>
          Atualizar
        </button>
      </header>

      <AsyncBoundary loading={loading && !data} error={error} onRetry={reload}>
        {data ? (
          <>
            {degraded.size > 0 ? (
              <div className="banner banner--warn">
                Alguns serviços não responderam; os campos afetados aparecem como “—”.
              </div>
            ) : null}

            <div className="stat-grid">
              <StatCard
                label="Usuários"
                value={data.totalUsers}
                hint="contas ativas, sem bots"
                degraded={degraded.has('totalUsers')}
              />
              <StatCard
                label="Partidas concluídas"
                value={data.completedMatches}
                hint="histórico completo"
                degraded={degraded.has('completedMatches')}
              />
              <StatCard
                label="Partidas em andamento"
                value={data.liveMatches}
                hint="agora mesmo"
                degraded={degraded.has('liveMatches')}
              />
              <StatCard
                label="Bots"
                value={data.totalBots}
                hint="contas de bot"
                degraded={degraded.has('totalBots')}
              />
              <StatCard
                label="Jogadores provisionados"
                value={data.playersProvisioned}
                hint="já entraram no jogo"
                degraded={degraded.has('playersProvisioned')}
              />
              <StatCard
                label="Salas aguardando"
                value={data.waitingRooms}
                hint="sem partida iniciada"
                degraded={degraded.has('waitingRooms')}
              />
            </div>
          </>
        ) : null}
      </AsyncBoundary>
    </div>
  );
}
